import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS, apiRequest } from '@/lib/api';

interface Notification {
  id: number;
  to_member: {
    id: number;
    user: {
      first_name: string;
      last_name: string;
    };
  };
  from_member: {
    id: number;
    user: {
      first_name: string;
      last_name: string;
    };
  } | null;
  date: string;
  message: string;
  read: boolean;
  reference_id: number | null;
  reference_type: number;
  reference_type_display: string;
  type: number;
  type_display: string;
}

interface UnreadCountResponse {
  unread_count: number;
}

/**
 * Hook to fetch notifications for the current user
 * Automatically refetches every 30 seconds to show new notifications
 */
export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await apiRequest(API_ENDPOINTS.NOTIFICATIONS.LIST);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: 'Failed to fetch notifications'
        }));
        throw new Error(errorData.error || 'Failed to fetch notifications');
      }

      return response.json() as Promise<Notification[]>;
    },
    staleTime: 30000, // 30 seconds - refetch frequently for new notifications
    refetchInterval: 30000, // Poll every 30 seconds for new notifications
  });
}

/**
 * Hook to fetch unread notification count
 * Used for the notification bell badge
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const response = await apiRequest(API_ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: 'Failed to fetch unread count'
        }));
        throw new Error(errorData.error || 'Failed to fetch unread count');
      }

      return response.json() as Promise<UnreadCountResponse>;
    },
    staleTime: 30000,
    refetchInterval: 30000, // Poll every 30 seconds
  });
}

/**
 * Hook to mark a single notification as read
 * Uses optimistic updates for instant UI feedback
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: number) => {
      const response = await apiRequest(
        API_ENDPOINTS.NOTIFICATIONS.MARK_READ(notificationId),
        { method: 'POST' }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: 'Failed to mark notification as read'
        }));
        throw new Error(errorData.error || 'Failed to mark notification as read');
      }

      return response.json();
    },
    onMutate: async (notificationId) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['notifications'] });
      await queryClient.cancelQueries({ queryKey: ['notifications', 'unread-count'] });

      // Snapshot previous values
      const previousNotifications = queryClient.getQueryData<Notification[]>(['notifications']);
      const previousCount = queryClient.getQueryData<UnreadCountResponse>(['notifications', 'unread-count']);

      // Optimistically update notifications
      if (previousNotifications) {
        queryClient.setQueryData<Notification[]>(
          ['notifications'],
          previousNotifications.map(notif =>
            notif.id === notificationId ? { ...notif, read: true } : notif
          )
        );
      }

      // Optimistically decrement unread count
      if (previousCount && previousCount.unread_count > 0) {
        queryClient.setQueryData<UnreadCountResponse>(
          ['notifications', 'unread-count'],
          { unread_count: previousCount.unread_count - 1 }
        );
      }

      return { previousNotifications, previousCount };
    },
    onError: (err, notificationId, context) => {
      // Rollback on error
      if (context?.previousNotifications) {
        queryClient.setQueryData(['notifications'], context.previousNotifications);
      }
      if (context?.previousCount) {
        queryClient.setQueryData(['notifications', 'unread-count'], context.previousCount);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });
}

/**
 * Hook to mark all notifications as read
 * Uses optimistic updates for instant UI feedback
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ,
        { method: 'POST' }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: 'Failed to mark all notifications as read'
        }));
        throw new Error(errorData.error || 'Failed to mark all notifications as read');
      }

      return response.json();
    },
    onMutate: async () => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['notifications'] });
      await queryClient.cancelQueries({ queryKey: ['notifications', 'unread-count'] });

      // Snapshot previous values
      const previousNotifications = queryClient.getQueryData<Notification[]>(['notifications']);
      const previousCount = queryClient.getQueryData<UnreadCountResponse>(['notifications', 'unread-count']);

      // Optimistically update all notifications to read
      if (previousNotifications) {
        queryClient.setQueryData<Notification[]>(
          ['notifications'],
          previousNotifications.map(notif => ({ ...notif, read: true }))
        );
      }

      // Optimistically set unread count to 0
      queryClient.setQueryData<UnreadCountResponse>(
        ['notifications', 'unread-count'],
        { unread_count: 0 }
      );

      return { previousNotifications, previousCount };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousNotifications) {
        queryClient.setQueryData(['notifications'], context.previousNotifications);
      }
      if (context?.previousCount) {
        queryClient.setQueryData(['notifications', 'unread-count'], context.previousCount);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });
}
